import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getGetCollectionTicketClaimsQueryKey,
  getListCollectionTicketClaimMemberStatesQueryKey,
  useGetCollectionTicketClaimsHook,
  useListCollectionTicketClaimMemberStatesHook,
} from '@/api/trafikportalen';
import { isStudentCollectionType } from '@/helpers/collectionTypes';
import { getPeriodStatus } from '@/helpers/periodStatus';
import type { TravellerMembershipDTO } from '@/api/trafikportalen';

export type PendingSchoolCardState = 'card-ordered' | 'awaiting-card-transfer';

const PENDING_STATES: PendingSchoolCardState[] = [
  'awaiting-card-transfer',
  'card-ordered',
];

export const usePendingSchoolCard = (
  memberships: TravellerMembershipDTO[],
): PendingSchoolCardState | undefined => {
  const getClaims = useGetCollectionTicketClaimsHook();
  const listMemberStates = useListCollectionTicketClaimMemberStatesHook();

  const studentMemberships = useMemo(
    () =>
      memberships.filter((membership) =>
        isStudentCollectionType(membership.collection_type),
      ),
    [memberships],
  );

  const claimQueries = useQueries({
    queries: studentMemberships.map((membership) => ({
      queryKey: getGetCollectionTicketClaimsQueryKey(membership.collection_id),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getClaims(membership.collection_id, signal),
    })),
  });

  const claims = useMemo(
    () =>
      studentMemberships.flatMap((membership, i) =>
        (claimQueries[i]?.data ?? [])
          .filter((claim) => getPeriodStatus(claim) === 'active')
          .map((claim) => ({
            claimId: claim.id,
            collectionMemberId: membership.collection_member_id,
          })),
      ),
    [studentMemberships, claimQueries],
  );

  const memberStateQueries = useQueries({
    queries: claims.map(({ claimId }) => ({
      queryKey: getListCollectionTicketClaimMemberStatesQueryKey(claimId),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        listMemberStates(claimId, signal),
    })),
  });

  const states = claims.flatMap(
    ({ collectionMemberId }, i) =>
      memberStateQueries[i]?.data
        ?.filter(
          (memberState) =>
            memberState.collection_member_id === collectionMemberId,
        )
        .map((memberState) => memberState.state) ?? [],
  );

  return PENDING_STATES.find((state) => states.includes(state));
};
